/**
 * @file 活动人员列表-表头设置
 */

import { Button, ColumnsType, Badge, Table, Popover } from '@seiue/ui'
import humps from 'humps'
import styled from 'styled-components'

import {
  fieldApi,
  FieldDomainEnum,
  InformalReflection,
  ReflectionGenderEnum,
  ReflectionIdcardTypeEnum,
  ReflectionPupilTypeEnum,
  ReflectionRoleEnum,
  ReflectionStatusEnum,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'

import { useHistory } from '@/router'
import { FieldRespondWithData } from '@/uikit/FieldManagement'

import { ColumnsRender } from './ColumnsRender'
import {
  genderToLabel,
  guardianTypeToLabel,
  idcardTypeToLabel,
  roleToLabel,
  statusToColor,
  statusToLabel,
} from './dataMap'
import {
  formatPrivateInfo,
  getChildrenColumn,
  getChildrenDataSource,
} from './utils'

/**
 * 活动人员列表表头
 *
 * @param domain - 领域
 * @param hideOperators - 是否隐藏操作栏
 * @param groupNameIn - 是否仅返回某些组下字段
 * @returns 表头
 */
export const useBiosColumns = (
  domain?: string,
  hideOperators = false,
  groupNameIn?: string
) => {
  const history = useHistory()
  const { data, loading } = fieldApi.useQueryFields({
    domain: domain || FieldDomainEnum.InformalStudent,
    query: {
      expand: ['options'],
      groupNameIn: groupNameIn,
    },
  })

  const colRenderSelector: {
    [key: string]: (text: AnyType) => JSX.Element | string
  } = {
    role: (text: ReflectionRoleEnum | undefined) =>
      text ? roleToLabel.get(text) : '-',
    gender: (text: ReflectionGenderEnum | undefined) =>
      text ? genderToLabel.get(text) : '-',
    status: (text: ReflectionStatusEnum | undefined) => (
      <Badge
        color={text ? statusToColor.get(text) : 'yellow'}
        text={text ? statusToLabel.get(text) : '-'}
      />
    ),
    idcardType: (text: ReflectionIdcardTypeEnum | null) =>
      text ? idcardTypeToLabel.get(text) : '-',
    'guardians.0.pupilType': (text: ReflectionPupilTypeEnum | undefined) =>
      text ? guardianTypeToLabel.get(text) : '-',
    'guardians.1.pupilType': (text: ReflectionPupilTypeEnum | undefined) =>
      text ? guardianTypeToLabel.get(text) : '-',
    phone: (text: string | null) => formatPrivateInfo(text, false),
    account: (text: string | null) => formatPrivateInfo(text),
    idcard: (text: string | null) => formatPrivateInfo(text),
  }

  const dataColumns: ColumnsType<InformalReflection> = data
    ? data
        .filter((i: FieldRespondWithData) => {
          const { originLabel } = i.metadata || {}
          if (['avatar', 'account'].includes(originLabel || '')) {
            return false
          }
          return i.isShow
        })
        .map((i: FieldRespondWithData) => {
          const { originLabel } = i.metadata || {}
          return {
            title: i.label,
            key: humps.camelize(originLabel || i.name),
            dataIndex: humps.camelize(originLabel || i.name),
            width: 150,
            render: (text: AnyType, record: InformalReflection) => {
              if (i.children?.length) {
                const fields = record.fields
                  ? record.fields[i.name as keyof object]
                  : {}
                const childrenData =
                  fields && Array.isArray(fields) ? fields : []
                return (
                  <Popover
                    placement="topRight"
                    title={
                      <TableStyled
                        columns={getChildrenColumn(
                          i.children,
                          i.label === '证件'
                        )}
                        dataSource={getChildrenDataSource(record, i)}
                        rowKey={(r, i) => {
                          return `${i}`
                        }}
                      />
                    }
                  >
                    <InnerPopover>
                      {i.label === '证件'
                        ? record.idcard
                          ? '1'
                          : '0'
                        : `${childrenData.length}`}
                    </InnerPopover>
                  </Popover>
                )
              }
              if (colRenderSelector[humps.camelize(i.name)]) {
                return colRenderSelector[humps.camelize(i.name)](text)
              } else {
                return (
                  <ColumnsRender
                    field={i}
                    text={text}
                  />
                )
              }
            },
          }
        })
    : []

  const operatorColumn: ColumnsType<InformalReflection> = [
    {
      title: '最近完成任务',
      key: 'completedTasks',
      dataIndex: 'completedTasks',
      width: 150,
    },
    {
      title: '暂未完成任务',
      key: 'ongoingTasks',
      dataIndex: 'ongoingTasks',
      width: 150,
    },
  ]

  if (!hideOperators) {
    operatorColumn.push({
      title: '操作',
      key: 'operators',
      fixed: 'right',
      width: 100,
      render: (v: InformalReflection) => {
        return (
          <Button
            type="link"
            onClick={() => {
              history.push(
                domain === FieldDomainEnum.InformalStudent
                  ? 'AdminBiosourceDetails'
                  : 'AdminTalentDetail',
                { id: v.id }
              )
            }}
          >
            详情
          </Button>
        )
      },
    })
  }

  const biosColumns: ColumnsType<InformalReflection> = dataColumns.length
    ? dataColumns.concat(operatorColumn)
    : []

  return { originData: data, biosColumns, loading }
}

const TableStyled = styled(Table)`
  // 字段数据为空时显示-,暂时隐藏，勿删
  .ant-table-cell:empty:after,
  .ant-table-cell > span:empty:after {
    content: '-';
  }
  .ant-table-thead > tr > th {
    background-color: ${(props) => props.theme.background._4};
  }
  .ant-table-thead
    > tr.ant-table-row-hover:not(.ant-table-expanded-row):not(
      .ant-table-row-selected
    )
    > td,
  .ant-table-tbody
    > tr.ant-table-row-hover:not(.ant-table-expanded-row):not(
      .ant-table-row-selected
    )
    > td,
  .ant-table-thead
    > tr:hover:not(.ant-table-expanded-row):not(.ant-table-row-selected)
    > td,
  .ant-table-tbody
    > tr:hover:not(.ant-table-expanded-row):not(.ant-table-row-selected)
    > td,
  .ant-table-tbody
    > tr.ant-table-row.even-row:hover:not(.ant-table-row-selected)
    td {
    background: ${(props) => props.theme.background._4};
  }
  .ant-table-tbody > tr.ant-table-row.even-row:not(.ant-table-row-selected) td {
    background-color: transparent;
  }
  .ant-table-tbody > tr > td {
    border-color: ${(props) => props.theme.border.greyNormal};
  }
`

const InnerPopover = styled.div`
  color: ${(props) => props.theme.brand._2};
  cursor: pointer;
`
